'use client';

import useWindowScroll from '@/lib/hooks/useWindowScroll';
import useStore from '@/stores/useStore';
import cx from 'classnames';
import Image from 'next/image';
import { FC, Fragment, ReactNode, useEffect, useRef } from 'react';
import HeaderMobileTakeover, { MobileMenuLinkProps } from './HeaderMobileTakeover';
import IconLogo from './IconLogo';
import Link from './Link';

export const HEADER_SCROLL_THRESHOLD = 50;

export interface DesktopMenuLinkProps {
  type: 'link' | 'primary' | 'secondary';
  title: string;
  href: string;
  action?: 'trial' | 'demo';
}

export interface HeaderProps {
  invert?: boolean;
  promoBanner?: React.ReactNode;
  desktopMenuLinks: DesktopMenuLinkProps[];
  mobileMenuLinks: MobileMenuLinkProps[];
  additionalLockupLogo?: ReactNode;
}

const LandingPageHeader: FC<HeaderProps> = ({ invert, promoBanner, desktopMenuLinks = [], mobileMenuLinks = [] }) => {
  const [scrollY] = useWindowScroll();
  const lastScrollY = useRef(0);

  const {
    isHeaderScrolled,
    isMobileMenuOpen,
    activeDesktopHeaderMenuIndex,
    setHeaderScrolled,
    setMobileMenuOpen,
    setDesktopHeaderMenuIndex,
    setDemoModalOpen,
    setTrialModalOpen,
  } = useStore();

  // Change header scrolled in global store
  useEffect(() => {
    if (scrollY >= HEADER_SCROLL_THRESHOLD && !isHeaderScrolled) {
      setHeaderScrolled(true);
    } else if (scrollY < HEADER_SCROLL_THRESHOLD && isHeaderScrolled) {
      setHeaderScrolled(false);
    }
  }, [scrollY, isHeaderScrolled]);

  const onToggleMobileMenu = () => setMobileMenuOpen(!isMobileMenuOpen);

  const onCloseAllMenus = () => {
    setMobileMenuOpen(false);
    setDesktopHeaderMenuIndex(null);
  };

  const onMouseLeaveHeader = () => {
    // Don't effect mobile menu
    if (isMobileMenuOpen) return;

    setMobileMenuOpen(false);
    setDesktopHeaderMenuIndex(null);
  };

  const onClickMenuButton = (action) => {
    switch (action) {
      case 'trial':
        setTrialModalOpen(true);
        break;
      case 'demo':
        setDemoModalOpen(true);
        break;

      default:
        break;
    }
  };

  // TODO: update logo
  const logoColumn = (
    <div className="flex">
      <Link href="/" className="block">
        <IconLogo
          className={cx('w-[112px] h-[26px] transition-colors duration-300', {
            'text-white': invert && !isMobileMenuOpen && !isHeaderScrolled,
            'text-violet-900': !invert || isMobileMenuOpen || isHeaderScrolled,
          })}
        />
      </Link>
    </div>
  );

  const mobileMenuTrigger = (
    <button
      aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
      onClick={onToggleMobileMenu}
      className="button button-size-circle-medium button-mobile-menu-trigger flex items-center justify-center"
    >
      {isMobileMenuOpen ? (
        <Image
          width={18}
          height={18}
          className="block"
          src="https://front.com/webassets/rebrand/icon-header-mobile-menu-close.svg"
          alt="Close mobile menu"
        />
      ) : (
        <Image
          width={19}
          height={14}
          className="block"
          src="https://front.com/webassets/rebrand/icon-header-mobile-menu-trigger.svg"
          alt="Mobile menu"
        />
      )}
    </button>
  );

  const rightMenuColumn = (
    <ul className="flex gap-1 justify-end items-center whitespace-nowrap">
      {desktopMenuLinks.map((link, index) => {
        return (
          <li
            key={`lp-menu-link-${link.title?.replace(/\s+/g, '-') ?? 'unknown'}-${index}`}
            className="hidden lg:block"
          >
            {link.href.length ? (
              <Link
                className={cx('block', {
                  'text-16/16 px-2 font-500': link.type === 'link',
                  'text-white': link.type === 'link' && invert && !isHeaderScrolled,
                  'button button-size-medium': link.type === 'primary' || link.type === 'secondary',
                  'button-primary-violet': link.type === 'primary' && (!invert || isHeaderScrolled),
                  'button-primary-white': link.type === 'primary' && invert && !isHeaderScrolled,
                  'button-secondary-gray': link.type === 'secondary' && (!invert || isHeaderScrolled),
                  'button-secondary-violet-300': link.type === 'secondary' && invert && !isHeaderScrolled,
                })}
                href={link.href}
                onClick={onCloseAllMenus}
              >
                {link.title}
              </Link>
            ) : (
              <button
                className={cx('block', {
                  'text-16/16 px-2 font-500': link.type === 'link',
                  'text-white': link.type === 'link' && invert && !isHeaderScrolled,
                  'button button-size-medium': link.type === 'primary' || link.type === 'secondary',
                  'button-primary-violet': link.type === 'primary' && (!invert || isHeaderScrolled),
                  'button-primary-white': link.type === 'primary' && invert && !isHeaderScrolled,
                  'button-secondary-gray': link.type === 'secondary' && (!invert || isHeaderScrolled),
                  'button-secondary-violet-300': link.type === 'secondary' && invert && !isHeaderScrolled,
                })}
                onClick={() => {
                  onClickMenuButton(link.action);
                }}
              >
                {link.title}
              </button>
            )}
          </li>
        );
      })}
      <li className="2xl:hidden">{mobileMenuTrigger}</li>
    </ul>
  );

  const header = (
    <header
      className={cx('sticky top-0 left-0 right-0 z-20 duration-600 ease-appear bg-white', {
        'shadow-header-scrolled': isHeaderScrolled && !isMobileMenuOpen,
      })}
      onMouseLeave={onMouseLeaveHeader}
    >
      <div className="px-container-mobile md:px-container-desktop 2xl:pr-5">
        <div className="grid grid-cols-2 gap-x-gutter-mobile md:gap-x-gutter-desktop h-12 md:h-10 items-center">
          {logoColumn}
          {rightMenuColumn}
        </div>
      </div>
      <div className={cx('hidden lg:block absolute top-10 pt-2 left-[50%] translate-x-[-50%] z-10')}></div>
    </header>
  );

  return (
    <Fragment>
      {!isMobileMenuOpen && promoBanner}
      <HeaderMobileTakeover menuLinks={mobileMenuLinks} onClickMenuButton={onClickMenuButton} />
      {header}
    </Fragment>
  );
};

export default LandingPageHeader;
